/* ----------------- Helpers --------------- */
import { getMappedExams } from "@helpers/exams";
import { getPracticeTestsQuizzesAndExams } from "../helpers/practice-tests-data";

/* ----------------- Hooks --------------- */
import { usePracticeTests } from "./usePracticeTests";

import { isScreenshotMode } from "@constants/common";
import { events } from "@constants/tracking";
import { isWeb } from "@helpers/platform";
import { useAppStore } from "@hooks/useAppStore";
import type { Distribution } from "@memorang/types/src/content";
import type { PracticeTestExam } from "@memorang/types/src/tag";
import { trackCustomEvent } from "analytics";
/* ----------------- Types --------------- */
import type { EXAM_STATUS } from "types/tag";
import { taskCardBodyTemplate } from "./constants";
import { useDailyProgress } from "./useDailyProgress";
interface SectionData {
	title: string;
	subtitle: string;
	status: EXAM_STATUS;
	handleClickTask: () => void;
	body: string;
	loading?: boolean;
	completion?: number;
	showRibbon?: boolean;
	free?: boolean;
	showReport?: boolean;
}

export const isCompletedInLastTwoWeeks = (dateCompleted: string): boolean => {
	const completedDate = new Date(dateCompleted);
	const currentDate = new Date();
	const timeDifference = currentDate.getTime() - completedDate.getTime();
	const daysDifference = timeDifference / (1000 * 3600 * 24);
	return daysDifference < 14;
};
const filterExamsByStatus = (
	exams: PracticeTestExam[],
	status: EXAM_STATUS,
) => {
	return exams.filter((exam) => exam.status === status);
};

const createSectionData = ({
	exam,
	body,
	selectedExamId,
	creatingSession,
	handlePracticeTestSession,
}: {
	exam: PracticeTestExam;
	body: string;
	selectedExamId?: string;
	creatingSession?: boolean;
	handlePracticeTestSession?: (
		examId: string,
		status: EXAM_STATUS,
		numericId?: number,
		isQuiz?: boolean,
		hasExamAccess?: boolean,
		replace?: boolean,
		collectionId?: number,
	) => void;
}): SectionData => {
	const {
		title,
		description,
		status,
		id,
		hasExamAccess,
		numericId,
		score,
		subType,
		collectionId,
	} = getMappedExams([exam])[0];

	const isQuiz = subType === "DIAGNOSTIC";
	return {
		title,
		subtitle: description,
		status,
		completion: score,
		loading: creatingSession && selectedExamId === id,
		handleClickTask: () => {
			trackCustomEvent({
				eventName: events.taskCardClicked,
				isDiagnostic: `${isQuiz}`,
				status,
				numericId,
				hasAccess: `${hasExamAccess}`,
			});
			handlePracticeTestSession!(
				id,
				status,
				numericId,
				isQuiz,
				hasExamAccess,
				false,
				collectionId,
			);
		},
		body,
		showRibbon: isQuiz ? true : !hasExamAccess,
		free: isQuiz,
	};
};

export const useRecommendationSection = (distribution?: Distribution) => {
	const {
		mixedItemDistResponse,
		handlePracticeTestSession,
		selectedExamId,
		creatingSession,
		loading,
		showPurchaseDialog,
		toggleShowPurchaseDialog,
		showCollectUserDetailsDialog,
		toggleShowCollectUserDetailsDialog,
		olxError,
		setOlxError,
	} = usePracticeTests();

	const practiceTestsLabel = useAppStore(
		(state) => state.exam.practiceTest?.label,
	);

	const practiceTestEnabled = useAppStore(
		(state) => state.exam.practiceTest?.enabled,
	);

	const dailyProgressHookData = useDailyProgress(distribution);

	const diagnosticLabel = useAppStore((state) => state.exam.diagnosticLabel);

	if (loading) {
		return {
			tasksSection: {
				loading: true,
				sectionTitle: "Recommended Tasks",
				type: "tasks" as const,
				onPress: undefined,
				data: [],
			},
			showPurchaseDialog,
			toggleShowPurchaseDialog,
		};
	}

	const exams = mixedItemDistResponse?.exams;
	const hasAccess = mixedItemDistResponse?.hasAccess;

	const { practiceTestQuizzes, practiceTests } =
		getPracticeTestsQuizzesAndExams(exams || []);
	let sectionData: SectionData[] = [];

	if (dailyProgressHookData?.dailyProgressSectionData) {
		sectionData.push(dailyProgressHookData?.dailyProgressSectionData);
	}

	const processCompletedItems = (exams: PracticeTestExam[]) => {
		const recentCompletedItems = exams.filter((item) =>
			isCompletedInLastTwoWeeks(item.dateCompleted),
		);
		if (recentCompletedItems.length > 0) {
			for (const exam of recentCompletedItems) {
				const isDiagonstic = exam.subType === "DIAGNOSTIC";
				const body = taskCardBodyTemplate({
					isDiagonstic,
					practiceTestsLabel,
				}).COMPLETE;
				sectionData.push(
					createSectionData({
						exam,
						handlePracticeTestSession,
						body,
					}),
				);
			}
			return true;
		}
	};
	const processPausedItems = (exams: PracticeTestExam[]) => {
		const pausedItems = filterExamsByStatus(exams, "ACTIVE");

		if (pausedItems.length > 0) {
			const exam = pausedItems[0];
			const isDiagonstic = exam.subType === "DIAGNOSTIC";

			const body = taskCardBodyTemplate({
				isDiagonstic,
				practiceTestsLabel,
			}).ACTIVE;

			sectionData.push(
				createSectionData({
					exam,
					body,
					handlePracticeTestSession,
					selectedExamId,
					creatingSession,
				}),
			);
			return true;
		}
	};
	const processNotStartedItems = (items: PracticeTestExam[]) => {
		const notStartedItems = filterExamsByStatus(items, "NOT_STARTED");

		const finalNotStartedItems = notStartedItems.filter((item) =>
			item.id.includes("OLX") ? item.hasAccess : true,
		);

		if (finalNotStartedItems.length > 0) {
			const exam = finalNotStartedItems[0];
			const isDiagonstic = exam.subType === "DIAGNOSTIC";
			const body = taskCardBodyTemplate({
				isDiagonstic,
				diagnosticLabel,
				practiceTestsLabel,
			}).NOT_STARTED;
			sectionData.push(
				createSectionData({
					exam,
					body,
					handlePracticeTestSession,
					selectedExamId,
					creatingSession,
				}),
			);
		}

		return sectionData;
	};

	const processItems = (
		practiceTests: PracticeTestExam[],
		practiceTestQuizzes: PracticeTestExam[],
	) => {
		const hasPausedPracticeTests = processPausedItems(practiceTests);
		const hasPausedQuiz = processPausedItems(practiceTestQuizzes);

		const processQuiz = () => {
			if (!hasPausedQuiz) {
				const hasCompletedQuizzes = processCompletedItems(practiceTestQuizzes);
				if (!hasCompletedQuizzes) {
					processNotStartedItems(practiceTestQuizzes);
				}
			}
		};
		if (hasPausedPracticeTests) {
			processQuiz();
		} else {
			const hasCompletedPracticeTests = processCompletedItems(practiceTests);
			if (hasCompletedPracticeTests) {
				processQuiz();
			} else if (hasAccess) {
				processNotStartedItems(practiceTests);
				processQuiz();
			} else {
				processQuiz();
				processNotStartedItems(practiceTests);
			}
		}
	};

	if (
		isWeb
			? practiceTestEnabled.web
			: practiceTestEnabled.mobile && mixedItemDistResponse
	) {
		processItems(practiceTests, practiceTestQuizzes);
	}

	if (isScreenshotMode) {
		sectionData = sectionData.map((item) => {
			return {
				...item,
				completion: 100,
				status: "COMPLETE",
			};
		});
	}

	if (sectionData.length) {
		return {
			tasksSection: {
				sectionTitle: "Recommended Tasks",
				type: "tasks" as const,
				onPress: undefined,
				data: sectionData,
			},
			showPurchaseDialog,
			toggleShowPurchaseDialog,
			olxError,
			setOlxError,
			showCollectUserDetailsDialog,
			toggleShowCollectUserDetailsDialog,
		};
	}
	return null;
};
